import './index.css'
import {useSelector} from "react-redux";
import {Controller, useFormContext} from "react-hook-form";
import AsyncSelect from "react-select/async";
import debounce from 'lodash.debounce';
import {RootState, useAppDispatch} from "@/store";
import {getInvoiceDetail, getInvoices} from "@/store/invoice/action";
import {
  ReactComponent as RiUserHeartFill
} from '@/assets/icons/ri-user-heart-fill.svg';
import {
  ReactComponent as InformationIcon
} from '@/assets/icons/ri-information-line.svg'
import {ReactComponent as RiBillFill} from '@/assets/icons/ri-bill-fill.svg';
import {
  ReactComponent as RiWhatsappLine
} from '@/assets/icons/ri-whatsapp-line.svg';
import RefundDetails from "@/pages/Refund/RefundDetails";
// import {invoiceActions} from "@/store/invoice/slice";
import BankInput from "@/pages/Refund/BankInput";
import {getShortDateFormat} from "@/utils";
import {sendWhatsapp} from "@/store/refund-detail/action";
import {colors, REFUND_STATUS} from "@/pages/Refund/constant";

interface InvoiceSelectOption {
  value: Invoice,
  label: string
}

const renderMiniInvoiceCard = (Icon: JSX.Element, title: string, subtitle: string) => (
  <div className="w-full flex gap-3 p-4 justify-start items-center">
    <div className="w-5 h-5 text-red-primary flex justify-center items-center">
      {Icon}
    </div>
    <div className="w-full">
      <div className="text-jsm font-medium">{title}</div>
      <div className="text-jxs font-normal text-grey-dark">{subtitle}</div>
    </div>
  </div>
)

const RefundDetail = () => {
  const {register, setValue, watch} = useFormContext()
  const dispatch = useAppDispatch()
  const {
    loading,
    state
  } = useSelector((rootState: RootState) => rootState.refundDetail)
  const {refund, modal} = state
  const {currentId} = modal
  const {venues} = useSelector((rootState: RootState) => rootState.userInfo.state)
  const {detail} = useSelector((rootState: RootState) => rootState.invoice.state)
  const venueName = venues.find(v => v.id === detail?.venue_id)?.name
  const isManualInput = watch('is_manual_input')
  const bankValue = watch('customer_info.bank_code')
  const bankSelectValue = bankValue ? {
    label: bankValue,
    value: bankValue
  } : null

  const toggleManualInput = (value) => {
    if (!value) {
      setValue('customer_info', null)
    }
    setValue('is_manual_input', value)
  }
  const createInvoiceOptions = (invoices: Invoice[]) => invoices.map(i => ({
    value: i,
    label: i.custom_id
  })) as InvoiceSelectOption[]
  const getInvoiceOptionsFromApi = (query, callback) => {
    dispatch(getInvoices({
      search: query,
      status: 'PAID'
    })).then((response: any) => {
      const invoices = response.payload.items as Invoice[]
      callback(createInvoiceOptions(invoices))
    })
  }
  const loadOptions = debounce(getInvoiceOptionsFromApi, 500)
  const handleClickSendWa = () => {
    dispatch(sendWhatsapp(currentId))
  }

  return (
    <>
      {
        !loading
          ? <div className="j-col w-full py-6 px-7">
            {
              refund?.id
                ? <></>
                : <>
                  <div className="j-col w-full gap-4">
                    <div className="text-jsm font-medium">Invoice ID</div>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      placeholder="Search for Invoice"
                      loadOptions={loadOptions}
                      onChange={(e: InvoiceSelectOption) => {
                        setValue('details', [])
                        setValue('invoice_id', e.value.id)
                        dispatch(getInvoiceDetail(e.value.id))
                        // dispatch(invoiceActions.setInvoiceDetail(e.value as Invoice))
                      }}
                    />
                  </div>
                  <div className="horizontal-separator--dash my-4"/>
                </>
            }
            <div className="w-full j-col gap-4">
              {
                detail ?
                  <>
                    <div className="text-jbase font-bold">{detail.custom_id}</div>
                    <table
                      className="table w-full border border-stroke-subtle rounded">
                      <tbody>
                      <tr>
                        <td className="border border-stroke-subtle w-1/2">
                          {renderMiniInvoiceCard(
                            <RiBillFill/>, detail.subscription ? 'Subscription' : 'Booking', venueName)}
                        </td>
                        <td className="border border-stroke-subtle w-1/2">
                          {renderMiniInvoiceCard(
                            <RiUserHeartFill/>, detail?.customer?.name ?? 'Customer', detail?.customer?.phone_number)}
                        </td>
                      </tr>
                      </tbody>
                    </table>
                    <RefundDetails/>
                    <div className="horizontal-separator--dash my-4"/>
                  </> : <></>
              }
              <div className="j-col gap-5">
                <div className="text-jbase font-bold">Refund Info</div>
                <div className="j-col gap-2">
                  <div className="text-jsm font-medium">Reason</div>
                  <textarea
                    {...register('note')}
                    className="textarea w-full"
                    placeholder="Enter reason"
                  />
                </div>
                {
                  !currentId
                    ? <div className="j-col gap-2">
                      <div className="text-jsm font-medium">Bank Account</div>
                      <div className="j-col gap-2">
                        <div
                          aria-hidden
                          className={`
                      bank-info-option flex-row
                      ${!isManualInput ? 'border-subtle--active' : 'border-subtle'}
                    `}
                          onClick={() => {
                            toggleManualInput(false)
                          }}
                        >
                          <div className="j-col gap-1">
                            <div className="text-jbase font-bold">Send Form</div>
                            <div className="text-jsm text-grey-dark">Send refund
                              form
                              to customer
                            </div>
                          </div>
                          {
                            !isManualInput ?
                              <input
                                checked
                                read-only
                                name="send-form"
                                type="checkbox"
                                className="checkbox circle"
                                onChange={() => {
                                  toggleManualInput(false)
                                }}
                              />
                              :
                              <div
                                className="rounded-full border border-grey-line-subtle h-[18px] w-[18px]"/>
                          }
                        </div>
                        <div
                          aria-hidden
                          className={`
                      bank-info-option flex-col gap-4
                      ${isManualInput ? 'border-subtle--active' : 'border-subtle'}
                    `}
                          onClick={() => {
                            toggleManualInput(true)
                          }}
                        >
                          <div className="j-row items-center">
                            <div className="j-col gap-1">
                              <div className="text-jbase font-bold">Manual Input
                              </div>
                              <div className="text-jsm text-grey-dark">Add manual
                                bank
                                account
                              </div>
                            </div>
                            {
                              isManualInput ?
                                <input
                                  checked
                                  read-only
                                  name="manual-input"
                                  type="checkbox"
                                  className="checkbox circle"
                                  onChange={() => {
                                    toggleManualInput(true)
                                  }}
                                />
                                :
                                <div
                                  className="rounded-full border border-grey-line-subtle h-[18px] w-[18px]"/>
                            }
                          </div>
                          <div className={`
                        j-col gap-4 transition-all overflow-hidden duration-500
                        ${isManualInput ? 'h-[250px]' : 'h-0'}
                      `}>
                            <div className="horizontal-separator--dash"/>
                            <BankInput selectedBank={bankSelectValue}/>
                          </div>
                        </div>
                      </div>
                    </div>
                    :
                    <BankInput selectedBank={bankSelectValue}/>
                }
                {
                  currentId && !refund?.is_manual_input ?
                    <>
                      <div className="horizontal-separator--dash"/>
                      <div
                        className="j-row justify-between p-4 bg-white-subtle rounded border border-grey-line-subtle">
                        {
                          refund?.send_notification_at ?
                            <div className="flex flex-col gap-1">
                              <div className="text-jsm text-grey-dark">
                                Last Sent
                              </div>
                              <div>
                                {getShortDateFormat(refund?.send_notification_at ?? 0)}
                              </div>
                            </div>
                            : <></>
                        }
                        <div
                          aria-hidden
                          onClick={handleClickSendWa}
                          className="cursor-pointer text-black-primary
                          gap-2 flex justify-center items-center px-4 py-2
                          rounded border border-grey-line-subtle bg-white">
                          <RiWhatsappLine/>
                          <div>Re-send WA</div>
                        </div>
                      </div>
                    </>
                    : <></>
                }
                {
                  currentId ?
                    <>
                      <div className="horizontal-separator"/>
                      <div className="j-row justify-between">
                        <div className="flex gap-1 items-center">
                          <InformationIcon/>
                          <div
                            className="text-jsm text-black-primary justify-start items-center">Status
                          </div>
                        </div>
                        <Controller
                          name='status'
                          render={({field}) => <select
                            multiple={false}
                            onChange={field.onChange}
                            value={field.value}
                            className={`p-2 rounded ${colors[field.value]}`}
                            name="status">
                            {
                              REFUND_STATUS.map(i =>
                                <option
                                  key={i}
                                  value={i}
                                  className={colors[i]}
                                >
                                  {i.replace('_', ' ')}
                                </option>
                              )
                            }
                          </select>}
                        />
                      </div>
                      <div className="horizontal-separator"/>
                    </>
                    : <></>
                }
              </div>
            </div>
          </div> : <></>
      }
    </>
  )
};

export default RefundDetail;